'use client';

import { useIntl } from 'react-intl';
import Image from '@alltrails/core/components/Image';
import Typography from '@alltrails/core/components/Typography';
import Button from '@alltrails/denali/components/Button';
import Carousel from '@alltrails/denali/components/Carousel';
import Heading from '@alltrails/denali/components/Heading';
import SponsoredBadge from '@/components/shared/SponsoredBadge';
import type { FeaturedNewsroomItem, FeaturedNewsroomItemsResponse } from '@/types/newsroom';
import { buildPostHref, getCategoryDisplayName } from '@/utils/newsroomHelpers';
import styles from './styles.module.scss';

type Props = {
  featuredPosts: FeaturedNewsroomItemsResponse;
};

export default function FeaturedPosts({ featuredPosts }: Props) {
  const intl = useIntl();
  const renderPost = (post: FeaturedNewsroomItem) => (
    <div className={styles.item}>
      <div className={styles.content}>
        <div className={styles.leftContent}>
          <Typography variant="heading200" component="div">
            {getCategoryDisplayName(intl, post.category_slug)}
          </Typography>
          <Heading as="h2" className={styles.title} size="700">
            {post.title}
          </Heading>
          {post.is_sponsored && <SponsoredBadge />}
          {post.featured_excerpt && (
            <Typography variant="text200" component="div" className={styles.excerpt}>
              {post.featured_excerpt}
            </Typography>
          )}
          <Button
            text={intl.formatMessage({ defaultMessage: 'Read more' })}
            linkInfo={{ href: buildPostHref(post.slug, post.type) }}
            variant="accent"
            size="md"
          />
        </div>
        <div className={styles.rightContent}>
          {post.featured_hero_image && (
            <Image src={post.featured_hero_image} alt={post.title} height={620} width={620} isLcp className={styles.heroImage} placeholder="empty" />
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className={styles.container}>
      <div className={styles.carouselWrapper}>
        <Carousel
          items={featuredPosts.posts}
          renderItem={renderPost}
          getItemKey={post => String(post.id)}
          leftArrowClassName={styles.leftArrow}
          rightArrowClassName={styles.rightArrow}
          slidesPerViewMobile={1}
          slidesPerViewBreakpoints={{ 760: 1 }}
          showOverflow={false}
        />
      </div>
    </div>
  );
}
